<script setup lang="ts">
import { buildLodgingBusinessLd } from "~/utils/lodgingBusinessSchema";

const { locale, t } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const config = useRuntimeConfig();

const { data: page } = await useAsyncData(
  `page-guest-services-${locale.value}`,
  async () => {
    const collection = ("pages_" + locale.value) as "pages_en";
    return await queryCollection(collection)
      .where("slug", "=", "guest-services")
      .first();
  },
  { watch: [locale] },
);

useLocalisedSeo(page);

// Phones often miss the default reveal threshold during fast finger-scroll —
// sections flick past while still at opacity:0. Trigger ~280px before the
// section enters the viewport on small screens; keep default on tablet/desktop.
useReveal(
  import.meta.client && window.matchMedia('(max-width: 768px)').matches
    ? { threshold: 0, rootMargin: '0px 0px 280px 0px' }
    : undefined,
);

useBreadcrumbsLd(() => [
  { name: t("nav.home"), path: "/" },
  { name: t("nav.services"), path: "/guest-services" },
]);

const SITE_ORIGIN = "https://georgiadream.ge";

// JSON-LD-only strings — kept inline (not in i18n/locales) because they
// describe the schema.org Service node and never render on the page.
// Bumping main.json with one-off SEO copy adds noise to the translator
// workflow; co-locating them here keeps the LD coherent with the page
// it annotates.
const SERVICE_LD_COPY: Record<string, { name: string; description: string }> = {
  en: {
    name: "Apartment guest concierge",
    description:
      "Check-in/out, cleaning, deposit handling, and resort access for short-term apartment guests at Dreamland Oasis Chakvi — operated directly by the apartment owner, not the hotel front desk.",
  },
  ru: {
    name: "Сервис апартаментов от хозяина",
    description:
      "Заезд и выезд, уборка, депозит, доступ к бассейнам и инфраструктуре комплекса для гостей апартаментов в Dreamland Oasis Чакви — напрямую от хозяина, без посредников.",
  },
  ka: {
    name: "აპარტამენტების კონცერჟი მესაკუთრისგან",
    description:
      "შემოსვლა/გასვლა, დასუფთავება, დეპოზიტი და კომპლექსის ინფრასტრუქტურასთან წვდომა Dreamland Oasis ჩაქვის აპარტამენტების სტუმრებისთვის — პირდაპირ მესაკუთრისგან.",
  },
};

const { data: lodgingStats } = await useAsyncData(
  "lodging-stats",
  // URL cast to string breaks nitro's typed-router route matching, which
  // blows TS's instantiation depth here (same workaround as pages/about.vue).
  () => $fetch<{ priceRange: string; numberOfRooms: number }>("/api/apartments/lodging-stats" as string),
  { default: () => ({ priceRange: "$$", numberOfRooms: 13 }) }
);

const lodgingLd = buildLodgingBusinessLd({
  email: (config.public.contactEmail as string) || "info@georgiadream.ge",
  whatsappNumber: (config.public.whatsappNumber as string) || "+995505556117",
  sameAs: [
    config.public.faceBookUrl as string | undefined,
    config.public.instagramUrl as string | undefined,
    config.public.telegramLink as string | undefined,
  ].filter((u): u is string => typeof u === "string" && u.length > 0),
  locale: locale.value as "en" | "ru" | "ka",
  priceRange: lodgingStats.value?.priceRange,
  numberOfRooms: lodgingStats.value?.numberOfRooms,
});

const guestServicesLd = computed(() => {
  if (!page.value) return null;
  const pageUrl = `${SITE_ORIGIN}${route.path}`;
  const heroAbsolute = page.value.hero_image
    ? `${SITE_ORIGIN}${page.value.hero_image}`
    : undefined;
  const rd = page.value.rules_detail;
  return {
    "@context": "https://schema.org",
    "@graph": [
      ...(lodgingLd["@graph"] as Array<Record<string, unknown>>),
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: page.value.seo_title ?? page.value.title,
        description: page.value.meta_description ?? page.value.description,
        isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
        about: { "@id": `${SITE_ORIGIN}/#lodgingbusiness` },
        ...(heroAbsolute
          ? {
              primaryImageOfPage: {
                "@type": "ImageObject",
                url: heroAbsolute,
              },
            }
          : {}),
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["h1", "[data-speakable]"],
        },
      },
      ...(rd?.video_src
        ? [
            {
              "@type": "VideoObject",
              "@id": `${pageUrl}#hero-video`,
              name: rd.image_alt ?? page.value.title,
              description: rd.image_alt ?? page.value.title,
              thumbnailUrl: `${SITE_ORIGIN}${rd.video_poster ?? rd.image ?? ""}`,
              uploadDate: "2026-04-27",
              contentUrl: `${SITE_ORIGIN}${rd.video_src}`,
              duration: "PT15S",
            },
          ]
        : []),
      // Owner-concierge USP — distinct from the resort's general operator
      // services. Schema.org Service node lets AI-search cite "Dreamland
      // Oasis apartments offer a dedicated owner-concierge for guests"
      // when answering booking-intent queries about Chakvi.
      (() => {
        const copy = SERVICE_LD_COPY[locale.value] ?? SERVICE_LD_COPY.en!;
        return {
          "@type": "Service",
          "@id": `${pageUrl}#concierge-service`,
          name: copy.name,
          description: copy.description,
          serviceType: "Apartment guest concierge",
          provider: { "@id": `${SITE_ORIGIN}/#lodgingbusiness` },
          areaServed: {
            "@type": "Place",
            name: "Dreamland Oasis Chakvi, Adjara, Georgia",
          },
          url: pageUrl,
          availableLanguage: ["en", "ru", "ka"],
        };
      })(),
      // FAQPage from the page's faq_block. Not for Google rich results (FAQ
      // snippets are gov/health-only since Aug 2023) but for AI-search
      // extraction — guest-service Q&A (pool access, cleaning, deposit) is
      // high-intent passage content AI engines cite. Source: content md.
      ...(page.value.faq_block?.items?.length
        ? [
            {
              "@type": "FAQPage",
              "@id": `${pageUrl}#faq`,
              inLanguage: locale.value,
              isPartOf: { "@id": `${pageUrl}#webpage` },
              mainEntity: page.value.faq_block.items.map((it) => ({
                "@type": "Question",
                name: it.question,
                acceptedAnswer: { "@type": "Answer", text: it.answer },
              })),
            },
          ]
        : []),
    ],
  };
});

useHead({
  script: () =>
    guestServicesLd.value
      ? [
          {
            type: "application/ld+json",
            innerHTML: JSON.stringify(guestServicesLd.value),
          },
        ]
      : [],
});

const whatsappUrl = computed(() => {
  const raw = (config.public.whatsappNumber as string) || "";
  const digits = raw.replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : "#";
});

const telegramUrl = computed(() => {
  return (config.public.telegramLink as string) || "";
});
</script>

<template>
  <div v-if="page">
    <section
      class="relative flex min-h-[40vh] w-full items-center overflow-hidden bg-cover bg-center py-10 sm:py-14 md:min-h-[52vh] md:py-20"
      :style="
        page.hero_image ? `background-image: url('${page.hero_image}')` : ''
      "
    >
      <div
        class="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/70 to-slate-900/90"
      />
      <div class="container relative z-10 text-center">
        <h1
          class="mt-6 font-display text-3xl italic leading-tight text-balance [overflow-wrap:anywhere] text-white drop-shadow sm:text-4xl md:mt-10 md:text-5xl lg:text-6xl"
        >
          {{ page.title }}
        </h1>
        <p
          v-if="page.intro"
          class="mx-auto mt-4 max-w-3xl text-base text-white/90 sm:text-lg md:mt-6 md:text-xl"
        >
          {{ page.intro }}
        </p>
      </div>
    </section>

    <div
      aria-hidden="true"
      class="pointer-events-none h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
    />

    <ServicesRulesDetailBlock
      v-if="page.rules_detail"
      :block="page.rules_detail"
    />

    <AboutExceptionalBlock
      v-if="page.exceptional_block"
      :block="page.exceptional_block"
      compact
    />

    <ServicesIncludedChecklist
      v-if="page.included"
      :block="page.included"
    />

    <div
      id="pool_pricing"
      aria-hidden="true"
      class="scroll-mt-24"
    />
    <ServicesPricingTable
      v-if="page.pool_pricing"
      :block="page.pool_pricing"
    />

    <ServicesPricingTable
      v-if="page.aquapark_pricing"
      :block="page.aquapark_pricing"
      compact-top
    />

    <ServicesActivityGrid
      v-if="page.activities"
      :block="page.activities"
    />

    <ServicesCards
      v-if="page.service_cards"
      :block="page.service_cards"
    />

    <LocationFaqAccordion
      v-if="page.faq_block"
      :block="page.faq_block"
    />

    <AboutFurtherBlock
      v-if="page.further"
      :block="page.further"
    />

    <section class="relative overflow-hidden bg-gradient-to-b from-white via-amber-50/30 to-white py-8 md:py-12 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900">
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
      />
      <div
        class="relative mx-auto flex w-full max-w-5xl flex-col items-center gap-6 px-4 text-center"
      >
        <div
          class="inline-flex items-center gap-3 font-body text-xs uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
        >
          <span class="inline-block h-px w-8 bg-amber-600 dark:bg-amber-400" />
          <span>{{ t("services.cta.eyebrow") }}</span>
          <span class="inline-block h-px w-8 bg-amber-600 dark:bg-amber-400" />
        </div>
        <h2
          class="font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ t("services.cta.heading") }}
        </h2>
        <p
          class="max-w-2xl font-body text-base text-slate-600 md:text-lg dark:text-slate-300"
        >
          {{ t("services.cta.subtitle") }}
        </p>
        <div class="mt-4 grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-4">
          <NuxtLink
            :to="localePath('/search')"
            class="flex items-center justify-center gap-2 rounded-full bg-amber-400 px-4 py-3 font-body text-sm font-semibold uppercase tracking-[0.15em] sm:tracking-[0.18em] md:tracking-[0.2em] text-slate-900 transition hover:bg-amber-300 sm:col-span-2"
          >
            {{ t("services.cta.view_apartments") }}
          </NuxtLink>
          <a
            :href="whatsappUrl"
            target="_blank"
            rel="noopener"
            class="flex items-center justify-center gap-2 rounded-full border border-slate-300 px-4 py-3 font-body text-sm font-semibold uppercase tracking-[0.15em] sm:tracking-[0.18em] md:tracking-[0.2em] text-slate-700 transition hover:border-emerald-500 hover:bg-emerald-500 hover:text-white sm:col-span-1 dark:border-white/30 dark:text-white dark:hover:border-emerald-500 dark:hover:bg-emerald-500 dark:hover:text-white"
          >
            <svg
              viewBox="0 0 24 24"
              class="h-4 w-4"
              fill="currentColor"
            >
              <path
                d="M17.5 14.4c-.3-.2-1.7-.9-2-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-1 1.2-.2.2-.4.2-.7 0-.3-.2-1.3-.5-2.5-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.2-.7-1.8-1-2.4-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1 2.9 1.1 3.1c.1.2 2 3 4.7 4.1 2.6 1.1 2.6.7 3.1.6.5-.1 1.7-.7 1.9-1.3.2-.6.2-1.2.2-1.3-.1-.1-.3-.2-.6-.4z M12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.1-1.3c1.4.8 3.1 1.3 4.9 1.3 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3.2.8.9-3.1-.2-.3C3.9 15 3.5 13.5 3.5 12 3.5 7.3 7.3 3.5 12 3.5S20.5 7.3 20.5 12 16.7 20.2 12 20.2z"
              />
            </svg>
            {{ t("services.cta.contact") }}
          </a>
          <a
            v-if="telegramUrl"
            :href="telegramUrl"
            target="_blank"
            rel="noopener"
            class="flex items-center justify-center gap-2 rounded-full border border-slate-300 px-4 py-3 font-body text-sm font-semibold uppercase tracking-[0.15em] sm:tracking-[0.18em] md:tracking-[0.2em] text-slate-700 transition hover:border-sky-500 hover:bg-sky-500 hover:text-white sm:col-span-1 dark:border-white/30 dark:text-white dark:hover:border-sky-500 dark:hover:bg-sky-500 dark:hover:text-white"
          >
            <Icon
              name="ic:baseline-telegram"
              class="h-4 w-4"
              aria-hidden="true"
            />
            {{ t("services.cta.telegram") }}
          </a>
        </div>
      </div>
    </section>
  </div>
</template>
